import type {
	APIReference,
	Article,
	BlogPosting,
	BreadcrumbList,
	CollectionPage,
	FAQPage,
	ItemList,
	NewsArticle,
	Organization,
	Person,
	ProfilePage,
	TechArticle,
	WebPage,
	WebSite,
} from "schema-dts"

/**
 * Central Schema.org builders. Every page renders its structured data through
 * <PageJsonLd> (src/components/core/seo/PageJsonLd.tsx) using these builders, so
 * that all entities consistently link back to the Organization and WebSite
 * nodes emitted by the root layout.
 */

export const SITE_URL = "https://www.dash0.com"
export const ORGANIZATION_ID = `${SITE_URL}/#organization`
export const WEBSITE_ID = `${SITE_URL}/#website`
/** The Dash0 product node, emitted in full on the homepage. */
export const SOFTWARE_APPLICATION_ID = `${SITE_URL}/#software`

const publisherRef = { "@id": ORGANIZATION_ID } as const
const websiteRef = { "@id": WEBSITE_ID } as const
export const softwareApplicationRef = { "@id": SOFTWARE_APPLICATION_ID } as const

export const organizationSchema: Organization = {
	"@type": "Organization",
	"@id": ORGANIZATION_ID,
	name: "Dash0",
	url: SITE_URL,
	logo: {
		"@type": "ImageObject",
		url: `${SITE_URL}/shared/logo_colors.svg`,
		width: "248",
		height: "48",
	},
	description:
		"Dash0 is an OpenTelemetry-native observability platform built for engineering teams who want full visibility into their systems without the operational overhead of managing complex pipelines or proprietary agents. It ingests logs, metrics, and traces natively via OpenTelemetry and gives you the tools to explore, alert, and act — all in one place.",
	foundingDate: "2023",
	slogan: "Observability, Simplified.",
	founder: [
		{
			"@type": "Person",
			name: "Mirko Novakovic",
			jobTitle: "CEO",
			url: "https://www.linkedin.com/in/mirkonovakovic",
		},
		{
			"@type": "Person",
			name: "Ben Blackmore",
			jobTitle: "CTO",
		},
		{
			"@type": "Person",
			name: "Miel Donkers",
			jobTitle: "Founding Engineer",
		},
		{
			"@type": "Person",
			name: "Marcel Birkner",
			jobTitle: "Founding Engineer",
		},
		{
			"@type": "Person",
			name: "Michele Mancioppi",
			jobTitle: "Chief Architect, Head of Open Source and Community",
		},
	],
	sameAs: [
		"https://www.linkedin.com/company/dash0hq",
		"https://github.com/dash0hq",
		"https://twitter.com/dash0hq",
		"https://www.youtube.com/@dash0hq",
		"https://www.crunchbase.com/organization/dash0",
	],
}

export const websiteSchema: WebSite = {
	"@type": "WebSite",
	"@id": WEBSITE_ID,
	url: SITE_URL,
	name: "Dash0",
	publisher: publisherRef,
	potentialAction: {
		"@type": "SearchAction",
		target: `${SITE_URL}/search?q={search_term_string}`,
		// @ts-expect-error query-input is required by Google but not part of schema-dts
		"query-input": "required name=search_term_string",
	},
}

export interface BreadcrumbItem {
	/** Human-readable name, e.g. the real page title (not a de-slugified guess). */
	name: string
	/** Absolute path starting with "/", e.g. "/blog/my-post". */
	path: string
}

/**
 * Builds a breadcrumb trail. "Home" is prepended automatically; pass only the
 * levels below it, ending with the current page.
 */
export function breadcrumbSchema(items: BreadcrumbItem[]): BreadcrumbList {
	return {
		"@type": "BreadcrumbList",
		itemListElement: [
			{ "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
			...items.map((item, index) => ({
				"@type": "ListItem" as const,
				position: index + 2,
				name: item.name,
				item: `${SITE_URL}${item.path}`,
			})),
		],
	}
}

export interface AuthorInput {
	name: string
	url?: string
}

/**
 * JSON-LD url for a content author: the on-site author page when one is
 * published, otherwise the author's LinkedIn profile (if any).
 */
export function authorSchemaUrl(author: {
	slug?: string
	includeInAuthorsListing?: boolean
	linkedinUrl?: string
}): string | undefined {
	if (author.includeInAuthorsListing && author.slug) {
		return `${SITE_URL}/authors/${author.slug}`
	}
	return author.linkedinUrl || undefined
}

function personList(authors: AuthorInput[] | undefined): Person[] | undefined {
	if (!authors?.length) return undefined
	return authors.map((author) => ({
		"@type": "Person",
		name: author.name,
		url: author.url,
	}))
}

export interface ArticleInput {
	headline: string
	description?: string
	/** Absolute path starting with "/". */
	path: string
	datePublished?: string
	dateModified?: string
	image?: string
	authors?: AuthorInput[]
}

function articleProperties(input: ArticleInput) {
	const url = `${SITE_URL}${input.path}`
	return {
		headline: input.headline,
		description: input.description,
		url,
		mainEntityOfPage: url,
		datePublished: input.datePublished,
		dateModified: input.dateModified ?? input.datePublished,
		image: input.image,
		author: personList(input.authors),
		publisher: publisherRef,
		isPartOf: websiteRef,
	}
}

export function articleSchema(input: ArticleInput): Article {
	return { "@type": "Article", ...articleProperties(input) } as Article
}

export function blogPostingSchema(input: ArticleInput): BlogPosting {
	return { "@type": "BlogPosting", ...articleProperties(input) } as BlogPosting
}

export function techArticleSchema(input: ArticleInput): TechArticle {
	return { "@type": "TechArticle", ...articleProperties(input) } as TechArticle
}

export interface NewsArticleInput extends ArticleInput {
	/** What the news reports on, e.g. softwareApplicationRef for product changes. */
	about?: { "@id": string }
}

/**
 * schema.org NewsArticle. Used by changelog entries, which each announce a
 * change to the Dash0 product.
 */
export function newsArticleSchema({
	about,
	...input
}: NewsArticleInput): NewsArticle {
	return {
		"@type": "NewsArticle",
		...articleProperties(input),
		about,
	} as NewsArticle
}

export interface ApiReferenceInput extends ArticleInput {
	/** Version of the API the page documents, e.g. the OpenAPI info.version. */
	apiVersion?: string
	/** Section of the reference the page belongs to, e.g. the OpenAPI tag. */
	section?: string
}

/**
 * schema.org APIReference, the TechArticle subtype for API documentation.
 * Used by the generated endpoint and schema pages under /docs/api-reference.
 */
export function apiReferenceSchema(input: ApiReferenceInput): APIReference {
	return {
		"@type": "APIReference",
		...articleProperties(input),
		assemblyVersion: input.apiVersion,
		articleSection: input.section,
		programmingModel: "REST",
		targetPlatform: "HTTP",
	} as APIReference
}

export interface WebPageInput {
	name: string
	description?: string
	/** Absolute path starting with "/". */
	path: string
	datePublished?: string
	dateModified?: string
}

export function webPageSchema(input: WebPageInput): WebPage {
	const url = `${SITE_URL}${input.path}`
	return {
		"@type": "WebPage",
		"@id": `${url}#webpage`,
		name: input.name,
		description: input.description,
		url,
		datePublished: input.datePublished,
		dateModified: input.dateModified,
		about: publisherRef,
		isPartOf: websiteRef,
	}
}

export interface CollectionItem {
	name: string
	/** Absolute path starting with "/". */
	path: string
}

export interface CollectionPageInput extends WebPageInput {
	/** Items listed on this page, in display order. */
	items?: CollectionItem[]
	/** Position of the first item; set on paginated pages after the first. */
	firstPosition?: number
}

export function collectionPageSchema(
	input: CollectionPageInput,
): CollectionPage {
	const url = `${SITE_URL}${input.path}`
	const firstPosition = input.firstPosition ?? 1
	const mainEntity: ItemList | undefined = input.items?.length
		? {
				"@type": "ItemList",
				itemListElement: input.items.map((item, index) => ({
					"@type": "ListItem" as const,
					position: firstPosition + index,
					name: item.name,
					url: `${SITE_URL}${item.path}`,
				})),
			}
		: undefined
	return {
		"@type": "CollectionPage",
		name: input.name,
		description: input.description,
		url,
		datePublished: input.datePublished,
		dateModified: input.dateModified,
		mainEntity,
		isPartOf: websiteRef,
	}
}

export interface FaqEntry {
	question: string
	answer: string
}

export function faqPageSchema(items: FaqEntry[]): FAQPage {
	return {
		"@type": "FAQPage",
		mainEntity: items.map((item) => ({
			"@type": "Question",
			name: item.question,
			acceptedAnswer: {
				"@type": "Answer",
				text: item.answer,
			},
		})),
	}
}

export interface PersonProfileInput {
	name: string
	/** Absolute path starting with "/", e.g. "/authors/jane-doe". */
	path: string
	description?: string
	jobTitle?: string
	image?: string
	/** External profile URLs, e.g. LinkedIn. */
	sameAs?: string[]
	/** External company the person works for; defaults to Dash0 when omitted. */
	company?: string
	datePublished?: string
	dateModified?: string
}

export function profilePageSchema(input: PersonProfileInput): ProfilePage {
	const url = `${SITE_URL}${input.path}`
	return {
		"@type": "ProfilePage",
		"@id": `${url}#profilepage`,
		url,
		datePublished: input.datePublished,
		dateModified: input.dateModified,
		mainEntity: {
			"@type": "Person",
			"@id": `${url}#person`,
			name: input.name,
			description: input.description,
			jobTitle: input.jobTitle,
			image: input.image,
			url,
			sameAs: input.sameAs,
			worksFor: input.company
				? { "@type": "Organization", name: input.company }
				: publisherRef,
		},
		isPartOf: websiteRef,
	}
}

